<script setup>
import { ref, watch, onBeforeUnmount, nextTick } from 'vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  campaign: { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue', 'close'])

const videoRef = ref(null)
const mediaLoading = ref(true)
let prevOverflow = ''

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      lockScroll()
      mediaLoading.value = true
      nextTick(() => {
        videoRef.value?.play?.().catch(() => {})
      })
    } else {
      unlockScroll()
    }
  }
)

watch(
  () => props.campaign?.mediaUrl,
  () => {
    if (props.modelValue) mediaLoading.value = true
  }
)

function handleMediaLoaded() {
  mediaLoading.value = false
}

function handleMediaError() {
  mediaLoading.value = false
}

function lockScroll() {
  prevOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
}
function unlockScroll() {
  document.body.style.overflow = prevOverflow
}

function close() {
  emit('update:modelValue', false)
  emit('close')
}

function onBackdropClick(e) {
  if (e.target === e.currentTarget) close()
}

function onKeydown(e) {
  if (e.key === 'Escape' && props.modelValue) close()
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) document.addEventListener('keydown', onKeydown)
    else document.removeEventListener('keydown', onKeydown)
  }
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  unlockScroll()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="popup-fade">
      <div
        v-if="modelValue && campaign"
        class="popup-backdrop"
        role="dialog"
        aria-modal="true"
        :aria-label="campaign.title"
        @click="onBackdropClick"
      >
        <Transition name="popup-rise" appear>
          <div class="popup-frame" @click.stop>
            <div class="popup-media">
              <video
                v-if="campaign.mediaType === 'video'"
                ref="videoRef"
                :src="campaign.mediaUrl"
                autoplay
                muted
                loop
                playsinline
                class="popup-media-content"
                @loadeddata="handleMediaLoaded"
                @error="handleMediaError"
              />
              <img
                v-else-if="campaign.mediaUrl"
                :src="campaign.mediaUrl"
                :alt="campaign.title"
                class="popup-media-content"
                @load="handleMediaLoaded"
                @error="handleMediaError"
              />
            </div>

            <Transition name="popup-fade">
              <div v-if="mediaLoading" class="popup-media-loader" aria-hidden="true">
                <div class="popup-spinner"></div>
              </div>
            </Transition>

            <div class="popup-overlay-gradient" aria-hidden="true"></div>

            <div class="popup-content">
              <h3 class="popup-title">{{ campaign.title }}</h3>
              <p v-if="campaign.description" class="popup-desc">
                {{ campaign.description }}
              </p>
            </div>

            <button class="popup-close" type="button" @click="close">סגירה</button>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.popup-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  z-index: 9999;
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.popup-frame {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
  overflow: hidden;
  isolation: isolate;
}

@media (min-width: 768px) {
  .popup-backdrop {
    align-items: center;
    padding: 32px;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }
  .popup-frame {
    width: min(420px, 92vw);
    height: min(820px, 92vh);
    border-radius: 28px;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6);
  }
}

.popup-media {
  position: absolute;
  inset: 0;
}
.popup-media-content {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.popup-media-loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1;
}
.popup-spinner {
  width: 44px;
  height: 44px;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top-color: #fff;
  border-radius: 50%;
  animation: popup-spin 0.85s linear infinite;
}
@keyframes popup-spin {
  to {
    transform: rotate(360deg);
  }
}

.popup-overlay-gradient {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 45%,
    rgba(0, 0, 0, 0.55) 75%,
    rgba(0, 0, 0, 0.85) 100%
  );
}

.popup-content {
  position: absolute;
  inset-inline: 16px;
  bottom: 96px;
  padding: 14px 18px;
  color: #fff;
  text-align: start;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.32);
  border-radius: 14px;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.popup-title {
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.65);
}

.popup-desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  opacity: 0.95;
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.65);
}

.popup-close {
  position: absolute;
  inset-inline: 16px;
  bottom: 20px;
  height: 52px;
  border: none;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  color: #0f172a;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.35);
  transition:
    background 0.18s ease,
    transform 0.18s ease;
}
.popup-close:hover {
  background: #fff;
}
.popup-close:active {
  transform: scale(0.985);
}

/* Backdrop fade */
.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.28s ease;
}
.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
}

/* Frame rise + scale */
.popup-rise-enter-active {
  transition:
    transform 0.42s cubic-bezier(0.18, 0.84, 0.3, 1.06),
    opacity 0.32s ease;
}
.popup-rise-leave-active {
  transition:
    transform 0.24s ease,
    opacity 0.24s ease;
}
.popup-rise-enter-from {
  transform: translateY(48px) scale(0.96);
  opacity: 0;
}
.popup-rise-leave-to {
  transform: translateY(24px) scale(0.98);
  opacity: 0;
}
</style>
